import React, { useState, useEffect } from 'react';
import { trackEvent } from './GoogleAnalytics';

const sections = [
    {
      id: 1,
      title: "Organizational Security Governance",
      weight: 0.11,
      criticalityRatings: [3, 2, 3, 2, 3, 3, 2, 1, 2, 2],
      questions: [
        "Do you have a dedicated security team with healthcare-specific expertise?",
        "Is there an established security governance structure with clear roles and responsibilities?",
        "Do you have a documented and regularly updated security strategy aligned with healthcare regulations?",
        "Are security policies and procedures formally documented, approved, and communicated?",
        "Do you conduct regular security awareness training for all staff (clinical and non-clinical)?",
        "Is there a formal risk assessment process that includes IoT medical devices?",
        "Are security metrics tracked and reported to executive leadership?",
        "Do you have a formal vendor risk management program for healthcare technology providers?",
        "Is there a formal change management process for all systems, including IoT medical devices?",
        "Is there dedicated budget allocated specifically for healthcare cybersecurity initiatives?"
      ]
    },
    {
      id: 2,
      title: "HIPAA Compliance & Regulatory Requirements",
      weight: 0.17,
      criticalityRatings: [3, 3, 3, 3, 3, 3, 3, 3, 2, 2, 2, 2, 2, 3, 3, 3, 3, 3, 3, 3],
      questions: [
        "Have you completed a comprehensive HIPAA Security Risk Assessment within the past year?",
        "Are HIPAA-mandated policies and procedures documented and regularly updated?",
        "Do you maintain an inventory of all systems containing PHI?",
        "Are appropriate Business Associate Agreements (BAAs) in place with all vendors who handle PHI?",
        "Do you have a formal process for addressing security incidents and breaches as required by HIPAA?",
        "Is encryption implemented for all PHI at rest and in transit?",
        "Do you maintain logs of all PHI access and regularly review them for unauthorized access?",
        "Are formal authorization and authentication controls implemented for accessing PHI?",
        "Do you have a process for right of access, amendment, and accounting of disclosures?",
        "Are HITECH and Omnibus requirements formally addressed in your compliance program?",
        "Have you assessed compliance with state-specific healthcare privacy laws (beyond HIPAA)?",
        "Is there a formal process for secure disposal of PHI and medical devices?",
        "Do you have a data retention policy that balances clinical needs with data minimization principles?",
        "Have you implemented multi-factor authentication for all workforce members accessing systems containing PHI, as required by the updated HIPAA Security Rule?",
        "Do you conduct vulnerability scans of all systems containing or transmitting PHI at least every six months?",
        "Have you performed a penetration test of your healthcare IT environment within the past year?",
        "Do you maintain a current, documented technology asset inventory and network map covering all PHI-processing systems?",
        "Do you have documented patch management procedures requiring critical and high-severity vulnerabilities to be remediated within defined timeframes on all PHI-containing systems?",
        "Is anti-malware and endpoint detection and response (EDR) software deployed and actively monitored on all systems that store, process, or transmit PHI?",
        "Do you have updated breach notification procedures that comply with accelerated reporting timelines, including prompt notification to HHS for breaches affecting 500 or more individuals?"
      ]
    },
    {
      id: 3,
      title: "IoT Medical Device Security",
      weight: 0.19,
      criticalityRatings: [3, 3, 3, 3, 3, 3, 2, 2, 3, 2, 2, 3, 2, 2, 2, 3, 3],
      questions: [
        "Do you maintain a comprehensive inventory of all connected medical devices?",
        "Are medical devices segmented from the rest of the network?",
        "Do you conduct security assessments of medical devices before procurement?",
        "Are medical devices regularly patched and updated when possible?",
        "Do you have compensating controls for medical devices that cannot be patched?",
        "Is there a formal process for managing default passwords on medical devices?",
        "Do you have network monitoring specifically for IoT and medical device traffic?",
        "Are there data loss prevention controls specifically for medical devices?",
        "Do you have a medical device incident response protocol?",
        "Are medical device vendors contractually obligated to address security vulnerabilities?",
        "Is there a formal process for decommissioning and disposing of medical devices?",
        "Do you conduct regular vulnerability scanning of medical devices?",
        "Is there a process to evaluate FDA security advisories for medical devices?",
        "Do you have an inventory of all third-party components in medical devices (e.g., software libraries)?",
        "Are secure configuration baselines established for medical devices?",
        "Do you have security controls for patient-owned medical devices that connect to your network?",
        "Is there a process to assess and mitigate risks from connected implantable medical devices?"
      ]
    },
    {
      id: 4,
      title: "EMR/EHR and Data Management Security",
      weight: 0.13,
      criticalityRatings: [3, 3, 3, 3, 2, 3, 3, 3, 2, 2, 2, 3],
      questions: [
        "Is your EMR/EHR system regularly patched and updated?",
        "Are strong access controls implemented for EMR/EHR systems?",
        "Do you implement role-based access for EMR/EHR data?",
        "Is there a formal process for provisioning and de-provisioning EMR/EHR access?",
        "Do you conduct regular access reviews for EMR/EHR systems?",
        "Is there a formal data backup and recovery strategy for patient data?",
        "Are audit logs enabled and regularly reviewed for EMR/EHR systems?",
        "Is data encrypted both in transit and at rest?",
        "Do you have controls to prevent unauthorized data exports from EMR/EHR systems?",
        "Is there a formal process for managing system interfaces with the EMR/EHR?",
        "Are patient portals secured with multi-factor authentication and access monitoring?",
        "Do you have specific controls to detect and prevent insider threats to patient data?"
      ]
    },
    {
      id: 5,
      title: "Data Sharing & Integration Security",
      weight: 0.08,
      criticalityRatings: [3, 2, 3, 3, 3, 2, 2, 3, 2],
      questions: [
        "Are secure protocols used for all health information exchanges?",
        "Do you implement data minimization principles when sharing patient data?",
        "Are there formal agreements in place for all data sharing partnerships?",
        "Is there an audit trail of all data shared outside the organization?",
        "Are APIs secured and regularly tested for vulnerabilities?",
        "Do you have a process for verifying the security of external systems before integration?",
        "Is patient consent properly documented and managed for data sharing?",
        "Do you have controls to prevent unauthorized mass data exfiltration?",
        "Are cloud environments that store or process patient data assessed for security and compliance?"
      ]
    },
    {
      id: 6,
      title: "Telemedicine & Remote Care Security",
      weight: 0.07,
      criticalityRatings: [3, 3, 3, 2, 2, 2, 2, 3, 2, 2],
      questions: [
        "Are telemedicine platforms assessed for security before implementation?",
        "Is end-to-end encryption implemented for all telehealth sessions?",
        "Are there strong authentication mechanisms for both providers and patients?",
        "Do you have a secure process for sharing and storing telemedicine session notes?",
        "Are there technical controls to prevent recording of telehealth sessions without consent?",
        "Do you conduct regular security testing of telemedicine platforms?",
        "Is there a formal process for patients to report security concerns with telemedicine?",
        "Are remote monitoring devices assessed for security vulnerabilities?",
        "Do telehealth clinicians receive specific training on security and privacy in virtual care?",
        "Do you have security controls for provider home offices and remote work environments?"
      ]
    },
    {
      id: 7,
      title: "AI and Advanced Technology Security",
      weight: 0.12,
      criticalityRatings: [3, 3, 2, 2, 2, 2, 3, 3, 3, 3, 2, 3],
      questions: [
        "Are AI systems assessed for security vulnerabilities before implementation?",
        "Do you have controls to protect the integrity of data used in AI systems?",
        "Is there a process to validate and verify AI algorithm outputs?",
        "Do you maintain documentation of AI data lineage and processing?",
        "Are there access controls specifically for AI systems and data?",
        "Do you have a framework to address bias and fairness in healthcare AI systems?",
        "Is there a human review process for critical AI-based clinical decisions?",
        "Do you have policies governing the use of generative AI and large language models (LLMs) in clinical and administrative operations?",
        "Are technical controls in place to prevent PHI from being entered into external AI tools or public AI services (e.g., ChatGPT, Copilot)?",
        "Is there a human oversight and correction process for AI-generated clinical documentation, diagnoses, or treatment recommendations?",
        "Do you conduct adversarial testing or red-team exercises specifically targeting AI systems used in clinical decision support?",
        "Do you assess third-party AI vendors for HIPAA compliance, data retention practices, and whether patient data is used for model training?"
      ]
    },
    {
      id: 8,
      title: "Incident Response & Recovery",
      weight: 0.13,
      criticalityRatings: [3, 3, 3, 3, 3, 3, 2, 3, 3, 2, 2, 3, 3],
      questions: [
        "Do you have a documented and tested incident response plan specific to healthcare operations?",
        "Does your organization conduct regular tabletop exercises for different breach scenarios?",
        "Is there a cross-functional cyber incident response team that includes clinical representation?",
        "Are there clear procedures for responding to ransomware attacks?",
        "Do you have offline backups of critical systems and data?",
        "Is there a formal business continuity plan for continuing clinical operations during cyber incidents?",
        "Do you have procedures for coordinating with law enforcement during cyber incidents?",
        "Is there cyber insurance coverage specifically addressing healthcare-related cyber incidents?",
        "Are there documented procedures for notifying patients and authorities about breaches?",
        "Do you conduct post-incident reviews to improve security after incidents?",
        "Are there predefined communication templates for various breach scenarios?",
        "Have you tested recovery procedures for critical clinical systems in the past year?",
        "Have you evaluated and exercised incident response scenarios involving third-party health IT vendors or clearinghouses, including supply chain compromise scenarios?"
      ]
    }
];

const HealthcareSecurityAssessment = () => {
  // Initialize scores state
  const [scores, setScores] = useState({});
  const [currentSection, setCurrentSection] = useState(1);
  const [showResults, setShowResults] = useState(false);
  const [showHowItWorksModal, setShowHowItWorksModal] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(() => {
    try { return !localStorage.getItem('hipaasec_visited'); } catch { return true; }
  });
  // Central gate: gated up front on first interaction (member-free / credits / Tools plan / one-time guest pay).
  const [started, setStarted] = useState(false);
  const ensureStarted = async () => {
    if (started) return true;
    const gate = window.csmGate;
    const ok = gate?.requireRun ? await gate.requireRun() : true; // graceful: no gate → allow
    if (ok) setStarted(true);
    return ok;
  };

  // Initialize scores on component mount (restore saved progress if present)
  useEffect(() => {
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem('hipaasec_scores') || 'null'); } catch { saved = null; }
    if (saved && typeof saved === 'object') {
      setScores(saved);
    } else {
      const initialScores = {};
      sections.forEach(section => {
        initialScores[section.id] = Array(section.questions.length).fill(null);
      });
      setScores(initialScores);
    }
    // Resume after a guest-pay / sign-in bounce (progress already persisted to localStorage).
    const gate = window.csmGate;
    if (new URLSearchParams(window.location.search).get('gr_session') && gate?.requireRun) {
      gate.requireRun().then((ok) => { if (ok) setStarted(true); });
    }
  }, []); // Empty dependency array to run only once
  
  // Track initial page view - separate effect to avoid infinite loop
  useEffect(() => {
    // Only track on initial mount
    trackEvent('view', 'assessment', 'start_assessment');
  }, []); // Empty dependency array = run once

  // Dismiss the first-time welcome modal
  const dismissWelcomeModal = () => {
    try { localStorage.setItem('hipaasec_visited', '1'); } catch {}
    setShowWelcomeModal(false);
    trackEvent('dismiss', 'modal', 'welcome_modal');
  };

  const openHowItWorksFromWelcome = () => {
    try { localStorage.setItem('hipaasec_visited', '1'); } catch {}
    setShowWelcomeModal(false);
    setShowHowItWorksModal(true);
  };

  // Handler to toggle the "How It Works" modal
  const toggleHowItWorksModal = () => {
    const newState = !showHowItWorksModal;
    setShowHowItWorksModal(newState);
    
    // Track modal interaction
    trackEvent(
      newState ? 'open' : 'close', 
      'modal', 
      'how_it_works_modal'
    );
  };

  // Handle score selection
  const handleScoreChange = async (sectionId, questionIndex, value) => {
    if (!(await ensureStarted())) return; // gate overlay shown — ignore until entitled
    setScores(prevScores => {
      const next = {
        ...prevScores,
        [sectionId]: [
          ...(prevScores[sectionId] || []).slice(0, questionIndex),
          value,
          ...(prevScores[sectionId] || []).slice(questionIndex + 1)
        ]
      };
      try { localStorage.setItem('hipaasec_scores', JSON.stringify(next)); } catch {} // persist for pay/login bounce
      return next;
    });

    // Track question answered
    trackEvent(
      'answer', 
      'question', 
      `section_${sectionId}_question_${questionIndex}`, 
      value
    );
  };

  // Calculate section score with criticality ratings
  const calculateSectionScore = (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    const sectionScores = scores[sectionId] || [];
    if (!sectionScores.some(s => s !== null)) return 0;

    let totalScore = 0;
    for (let i = 0; i < sectionScores.length; i++) {
      if (sectionScores[i] !== null) {
        totalScore += sectionScores[i] * (section.criticalityRatings?.[i] || 1);
      }
    }
    return totalScore;
  };

  // Calculate section completion percentage
  const calculateSectionCompletion = (sectionId) => {
    const sectionScores = scores[sectionId] || [];
    const answeredQuestions = sectionScores.filter(score => score !== null).length;
    const totalQuestions = sections.find(s => s.id === sectionId).questions.length;
    return (answeredQuestions / totalQuestions) * 100;
  };

  // Calculate weighted section score
  const calculateWeightedSectionScore = (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    const sectionScore = calculateSectionScore(sectionId);
    const maxPossibleScore = section.criticalityRatings
      ? section.criticalityRatings.reduce((acc, r) => acc + (r * 2), 0)
      : section.questions.length * 2;
    return (sectionScore / maxPossibleScore) * section.weight * 100;
  };

  // Calculate total risk score
  const calculateTotalRiskScore = () => {
    return sections.reduce((acc, section) => {
      return acc + calculateWeightedSectionScore(section.id);
    }, 0);
  };

  // Determine risk level based on total score
  const determineRiskLevel = () => {
    const totalScore = calculateTotalRiskScore();
    
    if (totalScore >= 90) return { level: "Low Risk", action: "Continue monitoring and improving", color: "bg-green-100 text-green-800" };
    if (totalScore >= 75) return { level: "Moderate Risk", action: "Address gaps in medium priority areas", color: "bg-yellow-100 text-yellow-800" };
    if (totalScore >= 50) return { level: "High Risk", action: "Immediate attention required for critical gaps", color: "bg-orange-100 text-orange-800" };
    return { level: "Critical Risk", action: "Urgent remediation needed", color: "bg-red-100 text-red-800" };
  };

  // Navigate between sections
  const goToNextSection = () => {
    if (currentSection < sections.length) {
      const nextSection = currentSection + 1;
      setCurrentSection(nextSection);
      window.scrollTo(0, 0);
      
      // Track section navigation
      trackEvent('navigate', 'section', `next_to_section_${nextSection}`);
    } else {
      setShowResults(true);
      window.scrollTo(0, 0);
      // Save to the member portal's Reports tab (logged-in runs only).
      const gate = window.csmGate;
      if (gate?.isLoggedIn?.() && gate.saveReport) {
        gate.saveReport({ title: 'HIPAA Compliance Assessment', summary: `HIPAA posture — score ${calculateTotalRiskScore()}`, data: { scores } });
      }

      // Track results view
      trackEvent('view', 'results', 'complete_assessment');
    }
  };

  const goToPreviousSection = () => {
    if (currentSection > 1) {
      const prevSection = currentSection - 1;
      setCurrentSection(prevSection);
      window.scrollTo(0, 0);
      
      // Track section navigation
      trackEvent('navigate', 'section', `back_to_section_${prevSection}`);
    }
  };

  // Generate key recommendations based on lowest scoring and critical areas
  const generateRecommendations = () => {
    const sectionScores = sections.map(section => {
      const sectionScore = calculateSectionScore(section.id);
      
      // Calculate max possible score considering criticality ratings
      let maxPossibleScore = 0;
      if (section.criticalityRatings && section.criticalityRatings.length > 0) {
        maxPossibleScore = section.criticalityRatings.reduce((acc, rating) => acc + (rating * 2), 0);
      } else {
        maxPossibleScore = section.questions.length * 2;
      }
      
      const percentageScore = (sectionScore / maxPossibleScore) * 100;
      
      // Calculate risk trend based on percentage of high-criticality items implemented
      let riskTrend = "Stable";
      if (section.criticalityRatings && section.criticalityRatings.length > 0) {
        const highCriticalityIndices = section.criticalityRatings
          .map((rating, index) => ({ rating, index }))
          .filter(item => item.rating === 3)
          .map(item => item.index);
          
        if (highCriticalityIndices.length > 0) {
          const criticalScores = highCriticalityIndices.map(index => scores[section.id]?.[index] || 0);
          const criticalImplementationRate = criticalScores.reduce((acc, score) => acc + score, 0) / (highCriticalityIndices.length * 2);
          
          if (criticalImplementationRate < 0.5) {
            riskTrend = "Worsening";
          } else if (criticalImplementationRate > 0.8) {
            riskTrend = "Improving";
          }
        }
      }
      
      return {
        id: section.id,
        title: section.title,
        score: percentageScore,
        weight: section.weight,
        riskTrend: riskTrend
      };
    });

    // Sort by combined risk score (considering weight, score, and risk trend)
    const prioritizedSections = [...sectionScores].sort((a, b) => {
      // Calculate weighted risk (lower score and higher weight means higher risk)
      const riskA = (100 - a.score) * a.weight * (a.riskTrend === "Worsening" ? 1.2 : 1);
      const riskB = (100 - b.score) * b.weight * (b.riskTrend === "Worsening" ? 1.2 : 1);
      return riskB - riskA;
    }).slice(0, 3);

    return prioritizedSections.map(section => {
      const sectionDetail = sections.find(s => s.id === section.id);
      
      // Find the critical and lowest scoring questions in this section
      const questionScores = sectionDetail.questions.map((question, index) => ({
        question,
        score: scores[section.id][index] || 0,
        criticality: sectionDetail.criticalityRatings?.[index] || 1
      }));
      
      // Prioritize questions that are both critical and low-scoring
      const priorityQuestions = questionScores
        .filter(q => q.score < 2) // Only consider questions that aren't fully implemented
        .sort((a, b) => {
          // Sort by criticality first, then by score
          if (b.criticality !== a.criticality) {
            return b.criticality - a.criticality;
          }
          return a.score - b.score;
        })
        .slice(0, 3);

      // Calculate regulatory impact
      const regulatoryImpact = section.id === 2 ? "High" : 
                              [1, 3, 4, 5].includes(section.id) ? "Medium" : "Low";

      return {
        sectionTitle: section.title,
        sectionScore: section.score.toFixed(1) + "%",
        riskTrend: section.riskTrend,
        regulatoryImpact: regulatoryImpact,
        priorityAreas: priorityQuestions.map(q => `${q.question} (Criticality: ${q.criticality})`)
      };
    });
  };

  // Reset assessment
  const resetAssessment = () => {
    const initialScores = {};
    sections.forEach(section => {
      initialScores[section.id] = Array(section.questions.length).fill(null);
    });
    setScores(initialScores);
    setCurrentSection(1);
    setShowResults(false);
    window.scrollTo(0, 0);
    
    // Track assessment reset
    trackEvent('reset', 'assessment', 'start_new_assessment');
  };

  // Render assessment form
  const renderAssessmentForm = () => {
    const currentSectionData = sections.find(section => section.id === currentSection);
    
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
        <div className="mb-6 flex justify-between items-center">
          <h2 className="text-xl font-bold">Section {currentSection}: {currentSectionData.title} ({(currentSectionData.weight * 100)}%)</h2>
          <div className="text-sm text-gray-500">
            Section {currentSection} of {sections.length}
          </div>
        </div>
        
        <div className="mb-4 w-full bg-gray-200 rounded-full h-2.5">
          <div 
            className="bg-blue-600 h-2.5 rounded-full" 
            style={{ width: `${calculateSectionCompletion(currentSection)}%` }}
          ></div>
        </div>

        <div className="space-y-6 mb-8">
          {currentSectionData.questions.map((question, index) => (
            <div key={index} className="p-4 bg-slate-50 rounded-md border border-slate-200">
              <div className="flex items-start mb-2">
                <span className="font-semibold mr-2">{currentSection}.{index + 1}</span>
                <span>{question}</span>
              </div>
              <div className="flex mt-3 space-x-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name={`section-${currentSection}-question-${index}`}
                    checked={scores[currentSection]?.[index] === 0}
                    onChange={() => handleScoreChange(currentSection, index, 0)}
                    className="h-4 w-4 text-red-600"
                  />
                  <span className="ml-2 text-sm text-red-700">Not Implemented (0)</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name={`section-${currentSection}-question-${index}`}
                    checked={scores[currentSection]?.[index] === 1}
                    onChange={() => handleScoreChange(currentSection, index, 1)}
                    className="h-4 w-4 text-yellow-600"
                  />
                  <span className="ml-2 text-sm text-yellow-700">Partially Implemented (1)</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name={`section-${currentSection}-question-${index}`}
                    checked={scores[currentSection]?.[index] === 2}
                    onChange={() => handleScoreChange(currentSection, index, 2)}
                    className="h-4 w-4 text-green-600"
                  />
                  <span className="ml-2 text-sm text-green-700">Fully Implemented (2)</span>
                </label>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-between mt-8">
          <button
            onClick={goToPreviousSection}
            disabled={currentSection === 1}
            className={`px-4 py-2 rounded ${currentSection === 1 ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-500 text-white hover:bg-blue-600'}`}
          >
            Previous
          </button>
          <button
            onClick={goToNextSection}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            {currentSection === sections.length ? 'Calculate Results' : 'Next'}
          </button>
        </div>
      </div>
    );
  };

  // Render results
  const renderResults = () => {
    const totalRiskScore = calculateTotalRiskScore();
    const riskLevel = determineRiskLevel();
    const recommendations = generateRecommendations();

    // Calculate HIPAA compliance score specifically
    const hipaaSection = sections.find(s => s.id === 2);
    const hipaaScore = calculateSectionScore(2);
    let maxHipaaScore = 0;
    if (hipaaSection.criticalityRatings && hipaaSection.criticalityRatings.length > 0) {
      maxHipaaScore = hipaaSection.criticalityRatings.reduce((acc, rating) => acc + (rating * 2), 0);
    } else {
      maxHipaaScore = hipaaSection.questions.length * 2;
    }
    const hipaaCompliancePercentage = (hipaaScore / maxHipaaScore) * 100;
    
    // Determine compliance status
    let complianceStatus = "Non-Compliant";
    if (hipaaCompliancePercentage >= 90) {
      complianceStatus = "Compliant";
    } else if (hipaaCompliancePercentage >= 75) {
      complianceStatus = "Partially Compliant";
    }

    // Calculate maturity level
    const maturityLevels = ["Initial", "Managed", "Defined", "Measured", "Optimized"];
    const maturityIndex = Math.min(Math.floor(totalRiskScore / 20), 4);
    const maturityLevel = maturityLevels[maturityIndex];

    return (
      <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
        <h2 className="text-2xl font-bold mb-6 text-center">Assessment Results</h2>
        
        <div className={`p-4 rounded-md mb-6 ${riskLevel.color}`}>
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-bold">{riskLevel.level}</h3>
              <p>{riskLevel.action}</p>
            </div>
            <div className="text-3xl font-bold">{totalRiskScore.toFixed(1)}%</div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-md">
            <h4 className="font-semibold mb-2">HIPAA Compliance Status</h4>
            <div className={`text-lg font-bold ${
              complianceStatus === "Compliant" ? "text-green-600" : 
              complianceStatus === "Partially Compliant" ? "text-yellow-600" : "text-red-600"
            }`}>
              {complianceStatus} ({hipaaCompliancePercentage.toFixed(1)}%)
            </div>
          </div>
          <div className="bg-purple-50 p-4 rounded-md">
            <h4 className="font-semibold mb-2">Security Maturity Level</h4>
            <div className="text-lg font-bold text-purple-600">
              {maturityLevel} ({maturityIndex + 1}/5)
            </div>
          </div>
        </div>
        
        <h3 className="text-xl font-bold mb-4">Section Scores</h3>
        <div className="mb-8 space-y-4">
          {sections.map(section => {
            const sectionScore = calculateSectionScore(section.id);
            
            // Calculate max possible score considering criticality ratings
            let maxPossibleScore = 0;
            if (section.criticalityRatings && section.criticalityRatings.length > 0) {
              maxPossibleScore = section.criticalityRatings.reduce((acc, rating) => acc + (rating * 2), 0);
            } else {
              maxPossibleScore = section.questions.length * 2;
            }
            
            const percentageScore = (sectionScore / maxPossibleScore) * 100;
            
            // Calculate risk trend
            let riskTrend = "Stable";
            if (section.criticalityRatings && section.criticalityRatings.length > 0) {
              const highCriticalityIndices = section.criticalityRatings
                .map((rating, index) => ({ rating, index }))
                .filter(item => item.rating === 3)
                .map(item => item.index);
                
              if (highCriticalityIndices.length > 0) {
                const criticalScores = highCriticalityIndices.map(index => scores[section.id]?.[index] || 0);
                const criticalImplementationRate = criticalScores.reduce((acc, score) => acc + score, 0) / (highCriticalityIndices.length * 2);
                
                if (criticalImplementationRate < 0.5) {
                  riskTrend = "Worsening";
                } else if (criticalImplementationRate > 0.8) {
                  riskTrend = "Improving";
                }
              }
            }
            
            return (
              <div key={section.id} className="bg-slate-50 p-4 rounded-md border border-slate-200">
                <div className="flex justify-between mb-2">
                  <span className="font-semibold">{section.title}</span>
                  <div className="flex items-center">
                    <span className="mr-3">{percentageScore.toFixed(1)}%</span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      riskTrend === "Improving" ? "bg-green-100 text-green-800" : 
                      riskTrend === "Worsening" ? "bg-red-100 text-red-800" : 
                      "bg-gray-100 text-gray-800"
                    }`}>
                      {riskTrend}
                    </span>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div 
                    className={`h-2.5 rounded-full ${
                      percentageScore >= 90 ? 'bg-green-500' : 
                      percentageScore >= 75 ? 'bg-yellow-500' : 
                      percentageScore >= 50 ? 'bg-orange-500' : 'bg-red-500'
                    }`} 
                    style={{ width: `${percentageScore}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
        
        <h3 className="text-xl font-bold mb-4">Priority Recommendations</h3>
        <div className="mb-8 space-y-4">
          {recommendations.map((rec, index) => (
            <div key={index} className="bg-slate-50 p-4 rounded-md border border-slate-200">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-semibold text-lg">{rec.sectionTitle} ({rec.sectionScore})</h4>
                <div className="flex space-x-2">
                  <span className={`text-xs px-2 py-1 rounded ${
                    rec.riskTrend === "Improving" ? "bg-green-100 text-green-800" : 
                    rec.riskTrend === "Worsening" ? "bg-red-100 text-red-800" : 
                    "bg-gray-100 text-gray-800"
                  }`}>
                    {rec.riskTrend}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded ${
                    rec.regulatoryImpact === "High" ? "bg-red-100 text-red-800" : 
                    rec.regulatoryImpact === "Medium" ? "bg-yellow-100 text-yellow-800" : 
                    "bg-blue-100 text-blue-800"
                  }`}>
                    {rec.regulatoryImpact} Regulatory Impact
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-700 mb-2">Critical priority areas:</p>
              <ul className="list-disc pl-5 space-y-1">
                {rec.priorityAreas.map((area, i) => (
                  <li key={i} className="text-sm">{area}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        
        <h3 className="text-xl font-bold mb-4">Resource Allocation Guidance</h3>
        <div className="mb-8 p-4 bg-slate-50 rounded-md border border-slate-200">
          <p className="mb-2 text-sm">Based on your assessment, here's a recommended distribution of cybersecurity resources:</p>
          <div className="space-y-3">
            {sections.map(section => {
              // Calculate risk score (inverse of compliance score, weighted by importance)
              const sectionScore = calculateSectionScore(section.id);
              let maxPossibleScore = 0;
              if (section.criticalityRatings && section.criticalityRatings.length > 0) {
                maxPossibleScore = section.criticalityRatings.reduce((acc, rating) => acc + (rating * 2), 0);
              } else {
                maxPossibleScore = section.questions.length * 2;
              }
              const percentageScore = (sectionScore / maxPossibleScore) * 100;
              const riskScore = (100 - percentageScore) * section.weight;
              
              // Convert to a resource allocation percentage
              const totalRisk = sections.reduce((acc, s) => {
                const score = calculateSectionScore(s.id);
                let maxScore = 0;
                if (s.criticalityRatings && s.criticalityRatings.length > 0) {
                  maxScore = s.criticalityRatings.reduce((acc, rating) => acc + (rating * 2), 0);
                } else {
                  maxScore = s.questions.length * 2;
                }
                const pct = (score / maxScore) * 100;
                return acc + ((100 - pct) * s.weight);
              }, 0);
              
              const resourceAllocation = totalRisk > 0 ? (riskScore / totalRisk) * 100 : 0;
              
              return (
                <div key={section.id}>
                  <div className="flex justify-between text-sm mb-1">
                    <span>{section.title}</span>
                    <span>{resourceAllocation.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ width: `${resourceAllocation}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        <div className="flex gap-4">
          <button
            onClick={resetAssessment}
            className="flex-1 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Start New Assessment
          </button>
          <button
            onClick={() => window.print()}
            className="flex-1 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Export Results
          </button>
        </div>
      </div>
    );
  };

  // First-time visitor welcome modal
  const WelcomeModal = () => {
    if (!showWelcomeModal) return null;
    const features = [
      "HIPAA Security Rule 2026",
      "AI & LLM Risk Controls",
      "IoT Medical Device Security",
      "EMR/EHR Data Protection",
      "Incident Response & Recovery",
      "Telemedicine Security",
    ];
    return (
      <div
        className="fixed inset-0 z-50 bg-gray-900 bg-opacity-80 flex items-center justify-center p-4"
        onClick={dismissWelcomeModal}
      >
        <div
          className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-blue-900 to-blue-600 rounded-t-2xl px-8 py-7 text-white">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-bold tracking-widest uppercase bg-white bg-opacity-20 px-3 py-1 rounded-full">
                CISO Marketplace · 2026 Edition
              </span>
              <button
                onClick={dismissWelcomeModal}
                className="text-white opacity-70 hover:opacity-100 focus:outline-none"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex items-center mb-2">
              <svg className="w-8 h-8 mr-3 text-blue-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <h2 className="text-2xl font-bold">HIPAA Security Assessment Tool</h2>
            </div>
            <p className="text-blue-200 text-sm">
              The healthcare cybersecurity self-assessment for 2026 — covering HIPAA, AI/LLM risks, IoT, and more
            </p>
          </div>

          {/* Body */}
          <div className="px-8 py-6">
            <p className="text-gray-600 text-sm mb-5">
              Answer questions across <strong>8 weighted security domains</strong> to receive your risk score, HIPAA
              compliance status, security maturity level, and prioritized recommendations — in minutes.
            </p>

            {/* What's covered */}
            <div className="grid grid-cols-2 gap-2 mb-6">
              {features.map(item => (
                <div key={item} className="flex items-center text-sm text-gray-700">
                  <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  {item}
                </div>
              ))}
            </div>

            {/* Sister tools */}
            <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-xl p-4 mb-6">
              <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide mb-3">
                Part of the ComplianceHub.wiki Toolkit
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="bg-slate-50 rounded-lg p-3 border border-indigo-100 flex-1">
                  <div className="font-semibold text-sm text-indigo-900 mb-1">DeviceRisk Assessment</div>
                  <div className="text-xs text-gray-500 mb-2">
                    FDA-aligned medical device cybersecurity risk scoring and inventory management
                  </div>
                  <a
                    href="https://devicerisk.compliancehub.wiki"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-indigo-600 font-medium hover:underline"
                  >
                    devicerisk.compliancehub.wiki →
                  </a>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 border border-blue-100 flex-1">
                  <div className="font-semibold text-sm text-blue-900 mb-1">This Tool — HIPAASecurity</div>
                  <div className="text-xs text-gray-500 mb-2">
                    Full HIPAA & healthcare security self-assessment with AI, IoT, and regulatory coverage
                  </div>
                  <span className="text-xs text-blue-600 font-medium">hipaa.compliancehub.wiki ✓ You are here</span>
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex items-center gap-3">
              <button
                onClick={dismissWelcomeModal}
                className="flex-1 px-6 py-3 bg-blue-700 hover:bg-blue-800 text-white font-semibold rounded-lg shadow-md text-sm transition-colors"
              >
                Start Assessment →
              </button>
              <button
                onClick={openHowItWorksFromWelcome}
                className="px-4 py-3 border border-gray-300 text-gray-600 hover:bg-gray-50 rounded-lg text-sm transition-colors"
              >
                How It Works
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Modal Component for "How It Works"
  const HowItWorksModal = () => {
    if (!showHowItWorksModal) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-500 bg-opacity-75 flex items-center justify-center">
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 my-8">
          {/* Modal header */}
          <div className="flex justify-between items-center border-b px-6 py-4">
            <h3 className="text-2xl leading-6 font-bold text-blue-900">
              How The Assessment Works
            </h3>
            <button 
              onClick={toggleHowItWorksModal}
              className="text-gray-400 hover:text-gray-500 focus:outline-none"
              aria-label="Close"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Modal body - with scrollable content */}
          <div className="px-6 py-4 max-h-[calc(100vh-200px)] overflow-y-auto">
            <div className="space-y-6">

              {/* 2026 What's New callout */}
              <div className="bg-blue-800 text-white rounded-xl p-5">
                <div className="flex items-center mb-3">
                  <span className="bg-white text-blue-800 text-xs font-bold px-2 py-0.5 rounded mr-2">NEW IN 2026</span>
                  <span className="font-bold text-lg">What's Updated This Year</span>
                </div>
                <ul className="text-sm text-blue-100 space-y-2">
                  <li className="flex items-start">
                    <span className="mr-2 mt-0.5">↑</span>
                    <span><strong className="text-white">AI &amp; LLM Security</strong> expanded from 3% → 12% weight — now covers generative AI governance, PHI leakage from public AI tools, adversarial testing, and third-party AI vendor compliance</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 mt-0.5">+</span>
                    <span><strong className="text-white">7 new HIPAA questions</strong> aligned to the 2025 HIPAA Security Rule NPRM: mandatory MFA, bi-annual vulnerability scanning, annual penetration testing, technology asset inventory + network map, critical patch timelines, EDR/anti-malware mandate, and accelerated breach notification</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 mt-0.5">+</span>
                    <span><strong className="text-white">5 new AI questions</strong> covering LLM governance policies, PHI leakage controls, AI documentation oversight, red-team testing, and AI vendor assessments</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 mt-0.5">+</span>
                    <span><strong className="text-white">1 new Incident Response question</strong> covering supply chain and third-party vendor compromise scenarios (Change Healthcare-style attacks)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 mt-0.5">↻</span>
                    <span><strong className="text-white">Frameworks updated</strong> to NIST CSF 2.0, NIST AI RMF 1.0, HITRUST CSF v11, ISO 27001:2022, and FDA Device Security Guidance 2023</span>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-blue-800 mb-2">Introduction</h4>
                <p className="mb-4">
                  This 2026 self-assessment tool is designed for healthcare CISOs, CCOs, and security professionals to evaluate cybersecurity risks related to IoT medical devices, AI and LLM deployments, data flow, and HIPAA compliance across healthcare environments. Questions have been updated to reflect the 2025 HIPAA Security Rule NPRM, NIST CSF 2.0, and emerging AI security threats. Each section represents a key risk domain with weighted questions to help identify security gaps and prioritize remediation efforts.
                </p>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-blue-800 mb-2">How to Use This Tool</h4>
                <ol className="list-decimal pl-5 mb-4 space-y-2">
                  <li>Answer each question honestly based on your current security posture</li>
                  <li>For each question, select the appropriate score:
                    <ul className="list-disc pl-5 mt-1 space-y-1">
                      <li><span className="text-red-600 font-medium">Not Implemented (0)</span> = High risk</li>
                      <li><span className="text-yellow-600 font-medium">Partially Implemented (1)</span> = Medium risk</li>
                      <li><span className="text-green-600 font-medium">Fully Implemented (2)</span> = Low risk</li>
                    </ul>
                  </li>
                  <li>Progress through all sections to calculate your overall risk score</li>
                  <li>Review recommendations based on your assessment results</li>
                </ol>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-blue-800 mb-2">Scoring & Risk Assessment Calculation</h4>
                <p className="mb-2">The assessment uses a weighted scoring system that accounts for:</p>
                <ol className="list-decimal pl-5 mb-4 space-y-2">
                  <li>Different criticality ratings for each question (1-3)</li>
                  <li>Section weights that reflect the importance of each security domain (2026):
                    <ul className="list-disc pl-5 mt-1 space-y-1">
                      <li>Organizational Security Governance: 11%</li>
                      <li>HIPAA Compliance & Regulatory Requirements: 17%</li>
                      <li>IoT Medical Device Security: 19%</li>
                      <li>EMR/EHR and Data Management Security: 13%</li>
                      <li>Data Sharing & Integration Security: 8%</li>
                      <li>Telemedicine & Remote Care Security: 7%</li>
                      <li>AI and Advanced Technology Security: 12%</li>
                      <li>Incident Response & Recovery: 13%</li>
                    </ul>
                  </li>
                  <li>Weighted section scores are calculated using:
                    <div className="bg-gray-100 p-2 my-2 rounded">
                      Weighted Score = (Section Points / Maximum Section Points) × Section Weight × 100
                    </div>
                  </li>
                  <li>Total risk score is the sum of all weighted section scores</li>
                </ol>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-blue-800 mb-2">Risk Level Determination</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse mb-4">
                    <thead className="bg-slate-200">
                      <tr>
                        <th className="border border-gray-300 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Score</th>
                        <th className="border border-gray-300 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Risk Level</th>
                        <th className="border border-gray-300 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-slate-50 divide-y divide-slate-200">
                      <tr>
                        <td className="border border-gray-300 px-4 py-2 text-sm font-medium text-gray-900">90-100%</td>
                        <td className="border border-gray-300 px-4 py-2 text-sm text-green-700 font-medium">Low Risk</td>
                        <td className="border border-gray-300 px-4 py-2 text-sm text-gray-500">Continue monitoring and improving</td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 px-4 py-2 text-sm font-medium text-gray-900">75-89%</td>
                        <td className="border border-gray-300 px-4 py-2 text-sm text-yellow-700 font-medium">Moderate Risk</td>
                        <td className="border border-gray-300 px-4 py-2 text-sm text-gray-500">Address gaps in medium priority areas</td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 px-4 py-2 text-sm font-medium text-gray-900">50-74%</td>
                        <td className="border border-gray-300 px-4 py-2 text-sm text-orange-700 font-medium">High Risk</td>
                        <td className="border border-gray-300 px-4 py-2 text-sm text-gray-500">Immediate attention required for critical gaps</td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 px-4 py-2 text-sm font-medium text-gray-900">&lt;50%</td>
                        <td className="border border-gray-300 px-4 py-2 text-sm text-red-700 font-medium">Critical Risk</td>
                        <td className="border border-gray-300 px-4 py-2 text-sm text-gray-500">Urgent remediation needed</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-blue-800 mb-2">Result Analysis</h4>
                <p className="mb-2">
                  After completing the assessment, you'll receive:
                </p>
                <ul className="list-disc pl-5 mb-6 space-y-1">
                  <li>Overall risk score and risk level</li>
                  <li>HIPAA compliance status percentage</li>
                  <li>Security maturity level assessment</li>
                  <li>Section-by-section breakdown with scores and risk trends</li>
                  <li>Prioritized recommendations for highest-risk areas</li>
                  <li>Resource allocation guidance based on risk distribution</li>
                </ul>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="text-md font-semibold text-blue-800 mb-2">Healthcare Security Framework References</h4>
                <p className="text-sm">
                  This assessment incorporates guidance from: HIPAA Security Rule (including 2025 NPRM updates),
                  HITECH Act, HITRUST CSF v11, NIST Cybersecurity Framework 2.0, FDA Guidance on Medical Device
                  Security (2023), OCR Audit Protocol, NIST SP 800-66r2, ISO 27001:2022/27799, NIST AI RMF 1.0,
                  and HHS AI Strategy for Healthcare.
                </p>
              </div>
            </div>
          </div>
          
          {/* Modal footer */}
          <div className="bg-slate-100 px-6 py-4 flex justify-end rounded-b-lg">
            <button
              type="button"
              className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none sm:text-sm"
              onClick={toggleHowItWorksModal}
            >
              Begin Assessment
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto p-4">
      {/* Modals */}
      <WelcomeModal />
      <HowItWorksModal />

      {/* Top SEO Section */}
      <div className="mb-10 text-center">
        <h1 className="text-3xl font-bold text-blue-900 mb-4">Healthcare Security & HIPAA Compliance Assessment Tool 2026</h1>
        <p className="text-gray-700 mb-6 max-w-3xl mx-auto">
          Evaluate your organization's security posture with our updated 2026 assessment covering HIPAA Security Rule changes, AI and LLM risks, IoT medical device security, and PHI protection. Designed for healthcare providers, hospitals, and medical facilities.
        </p>
        <div className="grid md:grid-cols-3 gap-6 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="font-semibold text-blue-800 mb-2">HIPAA Compliance 2026</h3>
            <p className="text-sm">Evaluate your organization against updated HIPAA Security Rule requirements including mandatory MFA, vulnerability scanning, and pen testing obligations.</p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="font-semibold text-blue-800 mb-2">AI & IoT Security</h3>
            <p className="text-sm">Assess risks from generative AI, LLMs, and connected medical devices — now weighted at 31% of your total score to reflect the 2026 threat landscape.</p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="font-semibold text-blue-800 mb-2">Risk Prioritization</h3>
            <p className="text-sm">Get actionable recommendations based on criticality and impact to optimize your cybersecurity resource allocation.</p>
          </div>
        </div>
        {/* How It Works Button */}
        <button
          onClick={toggleHowItWorksModal}
          className="mb-8 inline-flex items-center px-5 py-2.5 bg-blue-700 hover:bg-blue-800 text-white font-medium rounded-lg text-sm shadow-md focus:outline-none"
        >
          <svg className="w-5 h-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          How It Works
        </button>
      </div>

      <div className="bg-blue-800 text-white p-6 rounded-lg shadow-md mb-6">
        <h1 className="text-2xl font-bold mb-2">Healthcare Cybersecurity & IoT Risk Self-Assessment 2026</h1>
        <p className="text-blue-100">
          Updated for 2026: evaluate your organization's security posture covering IoT medical devices, AI and LLM risks, updated HIPAA Security Rule requirements, and data flow compliance.
        </p>
      </div>

      {!showResults && (
        <div className="mb-6">
          <div className="flex mb-2">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setCurrentSection(section.id)}
                className={`flex-1 py-2 text-center text-sm ${
                  currentSection === section.id 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                }`}
              >
                {section.id}
              </button>
            ))}
          </div>
          <div className="flex text-xs text-gray-500 mb-6">
            {sections.map((section) => (
              <div key={section.id} className="flex-1 text-center truncate px-1">
                {section.title}
              </div>
            ))}
          </div>
        </div>
      )}

      {!showResults ? renderAssessmentForm() : renderResults()}

      {/* Sister Tool Showcase */}
      <div className="mt-12 mb-6">
        <div className="bg-gradient-to-r from-indigo-100 to-blue-100 p-6 rounded-lg shadow-md border border-blue-200">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/4 flex justify-center mb-4 md:mb-0">
              <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200">
                <svg className="w-24 h-24 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
              </div>
            </div>
            <div className="md:w-3/4 md:pl-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-2">Medical Device Risk Assessment Tool</h2>
              <p className="text-gray-700 mb-4">
                Complete your healthcare security strategy with our specialized medical device risk assessment tool. While this assessment focuses on overall healthcare security and HIPAA compliance, our sister tool at DeviceRisk.compliancehub.wiki provides in-depth evaluation specifically for connected medical devices.
              </p>
              <div className="flex flex-wrap gap-4 mb-4">
                <div className="bg-slate-50 px-4 py-2 rounded-md border border-blue-200 flex items-center">
                  <svg className="w-5 h-5 text-green-600 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-sm">FDA Compliance</span>
                </div>
                <div className="bg-slate-50 px-4 py-2 rounded-md border border-blue-200 flex items-center">
                  <svg className="w-5 h-5 text-green-600 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-sm">Device Inventory</span>
                </div>
                <div className="bg-slate-50 px-4 py-2 rounded-md border border-blue-200 flex items-center">
                  <svg className="w-5 h-5 text-green-600 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-sm">Vulnerability Scanning</span>
                </div>
                <div className="bg-slate-50 px-4 py-2 rounded-md border border-blue-200 flex items-center">
                  <svg className="w-5 h-5 text-green-600 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-sm">Risk Scoring</span>
                </div>
              </div>
              <a
                href="https://devicerisk.compliancehub.wiki"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg text-sm shadow-md focus:outline-none"
              >
                Visit DeviceRisk Assessment Tool
                <svg className="w-4 h-4 ml-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom SEO Section */}
      <div className="mt-16 border-t pt-10 pb-8">
        <div className="grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="text-xl font-bold text-blue-900 mb-4">Why Healthcare Security Assessment Matters in 2026</h2>
            <p className="text-gray-700 mb-4">
              Healthcare organizations face an expanding threat surface in 2026 — from AI-driven attacks and LLM data leakage to supply-chain breaches and updated HIPAA Security Rule mandates. Our assessment tool helps:
            </p>
            <ul className="list-disc pl-5 space-y-2 text-gray-700">
              <li>Identify vulnerabilities in your security infrastructure</li>
              <li>Meet updated HIPAA Security Rule requirements including mandatory MFA and vulnerability scanning</li>
              <li>Govern generative AI and LLM deployments to prevent PHI exposure</li>
              <li>Protect electronic protected health information (ePHI)</li>
              <li>Secure connected medical devices from cyber threats</li>
              <li>Develop a strategic roadmap for security improvements</li>
              <li>Optimize your cybersecurity resource allocation</li>
            </ul>
          </div>
          <div>
            <h2 className="text-xl font-bold text-blue-900 mb-4">Who Should Use This Assessment</h2>
            <p className="text-gray-700 mb-4">
              This comprehensive assessment is designed for healthcare organizations of all sizes:
            </p>
            <ul className="list-disc pl-5 space-y-2 text-gray-700">
              <li><span className="font-semibold">Healthcare CISOs and CCOs</span> - Evaluate your organization's security posture</li>
              <li><span className="font-semibold">IT Security Teams</span> - Identify gaps in your security controls</li>
              <li><span className="font-semibold">Compliance Officers</span> - Ensure adherence to regulatory requirements</li>
              <li><span className="font-semibold">Hospital Administrators</span> - Understand your organization's risk exposure</li>
              <li><span className="font-semibold">Healthcare IT Managers</span> - Prioritize security investments</li>
            </ul>
            <p className="mt-4 text-gray-700">
              Complete the assessment above to receive a detailed analysis of your healthcare security posture and actionable recommendations.
            </p>
          </div>
        </div>

        <div className="text-center mt-10 pt-6 border-t">
          <p className="text-sm text-gray-600">
            © {new Date().getFullYear()} Healthcare Security Assessment Tool | HIPAA Compliance | IoT Medical Device Security | Cybersecurity Risk Assessment
          </p>
        </div>
      </div>
    </div>
  );
};

export default HealthcareSecurityAssessment;